import {
  CustomFieldProfileFormDefaultValues,
  CustomFieldProfileFormProps,
  CustomFieldProfileFormRawValues,
  CustomFieldProfileFormType,
  CustomFieldProfileFormValues
} from "./CustomFieldProfileForm";
import {useTranslation} from "react-i18next";
import {createFormContext} from "@mantine/form";
import {
  adjustFromTimeZoneSafely,
  CustomFieldProfileCatalogItem,
  CustomFieldProfileEntity,
  getObjectId,
  useGetCustomFieldProfileItemCatalogByCompanyQuery,
  useGetCustomFieldProfileItemCatalogByProfileQuery,
} from "@ots-frontend/ots-data";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {Dispatch, SetStateAction, useEffect, useMemo, useState} from "react";

export const toFormProfileDefaultsFromCustomFieldProfile = (def: CustomFieldProfileEntity | undefined): Partial<CustomFieldProfileFormValues> => {
  return {
    company: def?.companyId,
    objectType: def?.forObjectType,
    name: def?.name,
    description: def?.description,
    effectiveFromDate: adjustFromTimeZoneSafely(def?.effectiveFromDttm),
    effectiveToDate: adjustFromTimeZoneSafely(def?.effectiveToDttm),
  };
};

const getInitialValues = (defaultValues: CustomFieldProfileFormDefaultValues | undefined): CustomFieldProfileFormRawValues => {
  return {
    company: getObjectId(defaultValues?.company),
    objectType: defaultValues?.objectType,
    name: defaultValues?.name,
    description: defaultValues?.description,
    effectiveFromDate: defaultValues?.effectiveFromDate,
    effectiveToDate: defaultValues?.effectiveToDate,
  }
}

export const [CustomFieldProfileFormProvider, useCustomFieldProfileFormContext, useSharedCustomFieldProfileForm] = createFormContext<CustomFieldProfileFormRawValues>();

export type UseCustomFieldProfileFormResult = {
  form: CustomFieldProfileFormType,
  catalog: CustomFieldProfileCatalogItem[]
  setCatalog: Dispatch<SetStateAction<CustomFieldProfileCatalogItem[]>>
}

export type UseCustomFieldProfileFormProps = Pick<CustomFieldProfileFormProps, 'defaultValues' | 'options'> & {}

export function useCustomFieldProfileForm(props: UseCustomFieldProfileFormProps): UseCustomFieldProfileFormResult {
  const {
    defaultValues,
    options,
  } = props

  const {companyId} = useAuth()
  const {t} = useTranslation(["common"])

  const [catalog, setCatalog] = useState<CustomFieldProfileCatalogItem[]>([]);

  const form = useSharedCustomFieldProfileForm({
    initialValues: getInitialValues({
      ...defaultValues,
      company: defaultValues?.company ?? companyId,
      effectiveFromDate: defaultValues?.effectiveFromDate ?? new Date(),
    }),
    validate: {
      company: (value) => value ? undefined : t("common.validation.required", "Required"),
      objectType: (value) => value ? undefined : t("common.validation.required", "Required"),
      name: (value) => value ? undefined : t("common.validation.required", "Required"),
      description: (value) => value ? undefined : t("common.validation.required", "Required"),
      effectiveFromDate: (value) => value ? undefined : t("common.validation.required", "Required"),
      effectiveToDate: (value, values) => {
        if (value && values.effectiveFromDate && value.getTime() < values.effectiveFromDate.getTime()) {
          return t("common.form.validation.todatebeforefrom", "The to date can't be before the from date")
        }
        return undefined
      }
    },
  })

  const profileId = options?.customFieldProfileId;
  const isEdit = !!profileId;
  const formCompanyId = useMemo(() => getObjectId(form.values.company), [form.values.company]);
  const objectType = form.values.objectType;

  const catalogQueryForEdit = useGetCustomFieldProfileItemCatalogByProfileQuery(
    isEdit && profileId ? profileId : undefined,
    {skip: !isEdit || !profileId}
  );

  const catalogQueryForCreate = useGetCustomFieldProfileItemCatalogByCompanyQuery(
    !isEdit && formCompanyId && objectType ? {companyId: formCompanyId, objectType: objectType} : (undefined as any),
    {skip: isEdit || !formCompanyId || !objectType}
  );

  useEffect(() => {
    if (isEdit) {
      if (catalogQueryForEdit.data) {
        setCatalog(catalogQueryForEdit.data)
      }
    } else {
      if (!companyId || !objectType) {
        setCatalog([]);
        return;
      }
      if (catalogQueryForCreate.data)
        setCatalog(catalogQueryForCreate.data)
    }
  }, [isEdit, catalogQueryForEdit.data, catalogQueryForCreate.data, companyId, objectType]);

  return {
    form: form,
    catalog: catalog,
    setCatalog: setCatalog,
  }
}
